// Restaurant management hooks
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { queryKeys } from '@/lib/query-client';
import type { Restaurant } from '@/types/entities';

/**
 * Fetch all active restaurants (public, for staff login)
 */
export function useAllRestaurants() {
  return useQuery({
    queryKey: ['restaurants', 'all-public'],
    staleTime: 10 * 60 * 1000, // 10 minutes - restaurants rarely change
    gcTime: 30 * 60 * 1000, // 30 minutes cache
    refetchOnWindowFocus: false, // Restaurants don't change often
    refetchOnMount: false,
    queryFn: async (): Promise<Restaurant[]> => {
      const { data, error } = await supabase
        .from('restaurants')
        .select('*')
        .eq('is_active', true)
        .is('deleted_at', null)
        .order('name');

      if (error) {
        throw error;
      }

      return (data || []).map((r) => ({
        id: r.id,
        name: r.name,
        uniqueIdentifier: r.unique_identifier || null,
        addressAr: r.address_ar || null,
        addressFr: r.address_fr || null,
        phone: r.phone || null,
        tableCount: r.table_count ?? 0,
        isActive: r.is_active ?? true,
        allowNegativeStock: r.allow_negative_stock ?? false,
        createdBy: r.created_by,
        deletedAt: r.deleted_at || null,
        createdAt: r.created_at || '',
        updatedAt: r.updated_at || '',
      }));
    },
  });
}

/**
 * Fetch the current admin user's restaurant (one-to-one)
 * For assistants, retrieves their assigned restaurant
 */
export function useRestaurants() {
  return useQuery({
    queryKey: queryKeys.restaurants.list(),
    staleTime: 10 * 60 * 1000, // 10 minutes - restaurants rarely change
    gcTime: 30 * 60 * 1000, // 30 minutes cache
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    queryFn: async (): Promise<Restaurant[]> => {
      // Get current auth user
      const {
        data: { user: authUser },
      } = await supabase.auth.getUser();

      if (!authUser) {
        // Not authenticated - return empty array
        return [];
      }

      // Check if user is an assistant (from user_metadata)
      const userType = authUser.user_metadata?.['user_type'];

      if (userType === 'assistant') {
        // Assistant user - get restaurant from metadata
        const restaurantId = authUser.user_metadata?.['restaurant_id'];

        if (!restaurantId) {
          console.error('Assistant user has no restaurant_id in metadata');
          return [];
        }

        // Fetch the assistant's restaurant using maybeSingle to avoid PGRST116
        const { data, error } = await supabase
          .from('restaurants')
          .select('*')
          .eq('id', restaurantId)
          .is('deleted_at', null)
          .maybeSingle();

        if (error) {
          console.error('Error fetching assistant restaurant:', error);
          // Return a minimal restaurant object from metadata
          return [{
            id: restaurantId,
            name: 'Restaurant',
            uniqueIdentifier: null,
            addressAr: null,
            addressFr: null,
            phone: null,
            tableCount: 0,
            isActive: true,
            allowNegativeStock: false,
            createdBy: '',
            deletedAt: null,
            createdAt: '',
            updatedAt: '',
          }];
        }

        if (!data) {
          // No data returned (RLS might block), use minimal object
          return [{
            id: restaurantId,
            name: 'Restaurant',
            uniqueIdentifier: null,
            addressAr: null,
            addressFr: null,
            phone: null,
            tableCount: 0,
            isActive: true,
            allowNegativeStock: false,
            createdBy: '',
            deletedAt: null,
            createdAt: '',
            updatedAt: '',
          }];
        }

        return [{
          id: data.id,
          name: data.name,
          uniqueIdentifier: data.unique_identifier || null,
          addressAr: data.address_ar || null,
          addressFr: data.address_fr || null,
          phone: data.phone || null,
          tableCount: data.table_count || 0,
          isActive: data.is_active ?? true,
          allowNegativeStock: data.allow_negative_stock ?? false,
          createdBy: data.created_by,
          deletedAt: data.deleted_at || null,
          createdAt: data.created_at || '',
          updatedAt: data.updated_at || '',
        }];
      }

      // Staff user - check localStorage for staff auth
      const staffAuth = localStorage.getItem('digimataam_staff_auth');
      if (staffAuth) {
        try {
          const staff = JSON.parse(staffAuth);
          if (staff?.restaurantId) {
            // Fetch the staff's restaurant using maybeSingle to avoid PGRST116
            const { data, error } = await supabase
              .from('restaurants')
              .select('*')
              .eq('id', staff.restaurantId)
              .is('deleted_at', null)
              .maybeSingle();

            if (error) {
              console.error('Error fetching staff restaurant:', error);
              return [{
                id: staff.restaurantId,
                name: 'Restaurant',
                uniqueIdentifier: null,
                addressAr: null,
                addressFr: null,
                phone: null,
                tableCount: 0,
                isActive: true,
                allowNegativeStock: false,
                createdBy: '',
                deletedAt: null,
                createdAt: '',
                updatedAt: '',
              }];
            }

            if (!data) {
              return [{
                id: staff.restaurantId,
                name: 'Restaurant',
                uniqueIdentifier: null,
                addressAr: null,
                addressFr: null,
                phone: null,
                tableCount: 0,
                isActive: true,
                allowNegativeStock: false,
                createdBy: '',
                deletedAt: null,
                createdAt: '',
                updatedAt: '',
              }];
            }

            return [{
              id: data.id,
              name: data.name,
              uniqueIdentifier: data.unique_identifier || null,
              addressAr: data.address_ar || null,
              addressFr: data.address_fr || null,
              phone: data.phone || null,
              tableCount: data.table_count || 0,
              isActive: data.is_active ?? true,
              allowNegativeStock: data.allow_negative_stock ?? false,
              createdBy: data.created_by,
              deletedAt: data.deleted_at || null,
              createdAt: data.created_at || '',
              updatedAt: data.updated_at || '',
            }];
          }
        } catch (parseError) {
          console.error('Error parsing staff auth:', parseError);
        }
      }

      // Admin user - fetch from users table
      const { data: userProfile, error: profileError} = await supabase
        .from('users')
        .select('restaurant_id')
        .eq('id', authUser.id)
        .maybeSingle();

      if (profileError) {
        console.error('Error fetching user profile:', profileError);
        return [];
      }

      if (!userProfile?.restaurant_id) {
        // User exists but has no restaurant - return empty array (this should trigger welcome redirect)
        return [];
      }

      // Fetch the single restaurant
      const { data, error } = await supabase
        .from('restaurants')
        .select('*')
        .eq('id', userProfile.restaurant_id)
        .is('deleted_at', null)
        .single();

      if (error) {
        throw error;
      }

      // Return as array for compatibility (but it's always just one restaurant)
      return [{
        id: data.id,
        name: data.name,
        uniqueIdentifier: data.unique_identifier || null,
        addressAr: data.address_ar || null,
        addressFr: data.address_fr || null,
        phone: data.phone || null,
        tableCount: data.table_count || 0,
        isActive: data.is_active ?? true,
        allowNegativeStock: data.allow_negative_stock ?? false,
        createdBy: data.created_by,
        deletedAt: data.deleted_at || null,
        createdAt: data.created_at || '',
        updatedAt: data.updated_at || '',
      }];
    },
  });
}

/**
 * Fetch single restaurant by ID
 */
export function useRestaurant(restaurantId: string) {
  return useQuery({
    queryKey: queryKeys.restaurants.detail(restaurantId),
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes cache
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    queryFn: async (): Promise<Restaurant | null> => {
      const { data, error } = await supabase
        .from('restaurants')
        .select('*')
        .eq('id', restaurantId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching restaurant:', error);
        return null;
      }

      if (!data) {
        return null;
      }

      return {
        id: data.id,
        name: data.name,
        uniqueIdentifier: data.unique_identifier || null,
        addressAr: data.address_ar || null,
        addressFr: data.address_fr || null,
        phone: data.phone || null,
        tableCount: data.table_count || 0,
        isActive: data.is_active ?? true,
        allowNegativeStock: data.allow_negative_stock ?? false,
        createdBy: data.created_by,
        deletedAt: data.deleted_at || null,
        createdAt: data.created_at || '',
        updatedAt: data.updated_at || '',
      };
    },
    enabled: !!restaurantId,
  });
}

/**
 * Create new restaurant mutation
 */
export function useCreateRestaurant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: {
      name: string;
      addressAr?: string;
      addressFr?: string;
      phone?: string;
      tableCount?: number;
    }) => {
      // Call the database function
      const rpcParams: {
        restaurant_name: string
        restaurant_address_ar?: string
        restaurant_address_fr?: string
        restaurant_phone?: string
        restaurant_table_count?: number
      } = {
        restaurant_name: request.name,
      }

      if (request.addressAr) rpcParams.restaurant_address_ar = request.addressAr
      if (request.addressFr) rpcParams.restaurant_address_fr = request.addressFr
      if (request.phone) rpcParams.restaurant_phone = request.phone
      if (request.tableCount !== undefined) rpcParams.restaurant_table_count = request.tableCount

      const { data, error } = await supabase.rpc('create_restaurant' as any, rpcParams);

      if (error) {
        throw error;
      }

      if (!data || data.length === 0) {
        throw new Error('Failed to create restaurant');
      }

      const result = data[0];
      if (!result?.success) {
        throw new Error(result?.message || 'Failed to create restaurant');
      }

      return result.restaurant_id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.restaurants.all() });
    },
  });
}

/**
 * Update restaurant mutation
 */
export function useUpdateRestaurant(restaurantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: {
      name?: string;
      addressAr?: string;
      addressFr?: string;
      phone?: string;
      tableCount?: number;
      isActive?: boolean;
      allowNegativeStock?: boolean;
    }) => {
      const updateData: {
        name?: string;
        address_ar?: string | null;
        address_fr?: string | null;
        phone?: string | null;
        table_count?: number | null;
        is_active?: boolean | null;
        allow_negative_stock?: boolean;
      } = {};

      if (request.name !== undefined) updateData.name = request.name;
      if (request.addressAr !== undefined) updateData.address_ar = request.addressAr || null;
      if (request.addressFr !== undefined) updateData.address_fr = request.addressFr || null;
      if (request.phone !== undefined) updateData.phone = request.phone || null;
      if (request.tableCount !== undefined) updateData.table_count = request.tableCount;
      if (request.isActive !== undefined) updateData.is_active = request.isActive;

      // Handle allowNegativeStock via RPC (bypasses PostgREST schema cache)
      if (request.allowNegativeStock !== undefined) {
        const { error: rpcError } = await supabase.rpc('update_allow_negative_stock' as any, {
          p_restaurant_id: restaurantId,
          p_allow_negative_stock: request.allowNegativeStock,
        });
        if (rpcError) {
          throw rpcError;
        }
        // If only allowNegativeStock was set, return early
        if (Object.keys(updateData).length === 0) {
          return true;
        }
      }

      const { error } = await supabase
        .from('restaurants')
        .update(updateData)
        .eq('id', restaurantId);

      if (error) {
        throw error;
      }

      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.restaurants.all() });
      queryClient.invalidateQueries({
        queryKey: queryKeys.restaurants.detail(restaurantId),
      });
    },
  });
}

/**
 * Soft delete restaurant mutation
 */
export function useDeleteRestaurant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (restaurantId: string) => {
      const { error } = await supabase
        .from('restaurants')
        .update({
          deleted_at: new Date().toISOString(),
          is_active: false,
        })
        .eq('id', restaurantId);

      if (error) {
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.restaurants.all() });
    },
  });
}
